import React, { useState, useEffect } from 'react';
import { noteTemplatesAPI, NoteTemplate } from '../../services/api';
import { Button, Card, CardBody, Spinner } from '../ui';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';

interface NoteTemplateManagerProps {
    onSelect: (content: string) => void;
    onClose: () => void;
}

const NoteTemplateManager: React.FC<NoteTemplateManagerProps> = ({ onSelect, onClose }) => {
    const [templates, setTemplates] = useState<NoteTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState({ name: '', content: '', isDefault: false });
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const data = await noteTemplatesAPI.getAll();
            setTemplates(data);
        } catch (err: any) {
            setError('Failed to load templates');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setIsCreating(true);
        setEditingId(null);
        setFormData({ name: '', content: '', isDefault: false });
    };

    const handleEdit = (template: NoteTemplate) => {
        setEditingId(template.id);
        setIsCreating(false);
        setFormData({ name: template.name, content: template.content, isDefault: template.isDefault });
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;
        try {
            await noteTemplatesAPI.delete(id);
            loadTemplates();
        } catch (err) {
            setError('Failed to delete template');
        }
    };

    const handleSave = async () => {
        if (!formData.name || !formData.content) return;
        try {
            if (isCreating) {
                await noteTemplatesAPI.create(formData);
            } else if (editingId) {
                await noteTemplatesAPI.update(editingId, formData);
            }
            setIsCreating(false);
            setEditingId(null);
            loadTemplates();
        } catch (err) {
            setError('Failed to save template');
        }
    };

    const handleCancel = () => {
        setIsCreating(false);
        setEditingId(null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
                    <h2 className="text-lg font-semibold text-neutral-900">Manage Note Templates</h2>
                    <button onClick={onClose} className="text-neutral-500 hover:text-neutral-700">
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {error && <div className="mb-4 text-sm text-error-600 bg-error-50 p-3 rounded">{error}</div>}

                    {(isCreating || editingId) ? (
                        <div className="bg-neutral-50 p-4 rounded-lg border border-neutral-200 mb-6">
                            <h3 className="text-sm font-medium text-neutral-900 mb-3">{isCreating ? 'New Template' : 'Edit Template'}</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        placeholder="e.g., Standard Terms"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 mb-1">Content</label>
                                    <textarea
                                        value={formData.content}
                                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        placeholder="Enter template content..."
                                    />
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="isDefault"
                                        checked={formData.isDefault}
                                        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
                                    />
                                    <label htmlFor="isDefault" className="ml-2 block text-sm text-neutral-900">
                                        Set as default
                                    </label>
                                </div>
                                <div className="flex justify-end space-x-3">
                                    <Button type="button" variant="outline" size="sm" onClick={handleCancel}>Cancel</Button>
                                    <Button type="button" variant="primary" size="sm" onClick={handleSave}>Save</Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-6">
                            <Button variant="outline" size="sm" onClick={handleCreate} leftIcon={<PlusIcon className="h-4 w-4" />}>
                                Create New Template
                            </Button>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center py-8"><Spinner size="md" /></div>
                    ) : templates.length === 0 ? (
                        <div className="text-center py-8 text-neutral-500">No templates found. Create one to get started.</div>
                    ) : (
                        <div className="space-y-3">
                            {templates.map((template) => (
                                <div key={template.id} className="border border-neutral-200 rounded-lg p-4 hover:bg-neutral-50 transition-colors">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2">
                                                <h3 className="text-sm font-medium text-neutral-900">{template.name}</h3>
                                                {template.isDefault && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                                        Default
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-1 text-sm text-neutral-600 line-clamp-2">{template.content}</p>
                                        </div>
                                        <div className="flex items-center space-x-2 ml-4">
                                            <button
                                                onClick={() => onSelect(template.content)}
                                                className="text-primary-600 hover:text-primary-800 p-1"
                                                title="Use this template"
                                            >
                                                <CheckIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(template)}
                                                className="text-neutral-500 hover:text-neutral-700 p-1"
                                                title="Edit"
                                            >
                                                <PencilIcon className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(template.id)}
                                                className="text-error-500 hover:text-error-700 p-1"
                                                title="Delete"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bg-neutral-50 px-6 py-4 border-t border-neutral-200 flex justify-end">
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </div>
            </div>
        </div>
    );
};

export default NoteTemplateManager;
